import React, { useState, useEffect } from "react";
import { Spinner } from "react-bootstrap";
import { Search, X, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import apiClient from "../../utils/apiClient";
import { AppBadge } from "../ui";

const PAGE_SIZE = 10;

const AssignmentQuestionPicker = ({ selectedQuestions, setSelectedQuestions }) => {
  const [search, setSearch] = useState("");
  const [questions, setQuestions] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    apiClient
      .get("/questions/list", { params: { search, page, limit: PAGE_SIZE } })
      .then((res) => {
        setQuestions(res.data.questions);
        setTotal(res.data.total || 0);
      })
      .catch(() => { setQuestions([]); setTotal(0); })
      .finally(() => setLoading(false));
  }, [search, page]);

  const toggleQuestion = (qid) => {
    setSelectedQuestions((prev) =>
      prev.includes(qid) ? prev.filter((id) => id !== qid) : [...prev, qid]
    );
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const handlePageChange = (p) => {
    if (p >= 1 && p <= totalPages) setPage(p);
  };

  const diffColor = { Hard: "rose", Medium: "amber", Easy: "emerald" };

  return (
    <div className="asgn-qp">
      <div className="asgn-qp__toolbar">
        <span className="asgn-qp__count">
          <strong>{selectedQuestions.length}</strong> selected
        </span>
        <div className="asgn-qp__search">
          <Search size={14} className="asgn-qp__search-icon" />
          <input
            className="asgn-qp__search-input"
            placeholder="Search questions…"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
          />
          {search && (
            <button type="button" className="asgn-qp__search-clear" onClick={() => setSearch("")}>
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="asgn-qp__loading"><Spinner animation="border" size="sm" /></div>
      ) : questions.length === 0 ? (
        <div className="asgn-qp__empty">No questions found.</div>
      ) : (
        <div className="asgn-qp__table-wrap">
          <table className="asgn-qp__table">
            <thead>
              <tr>
                <th className="asgn-qp__th asgn-qp__th--check"></th>
                <th className="asgn-qp__th">Title</th>
                <th className="asgn-qp__th">Difficulty</th>
                <th className="asgn-qp__th">Categories</th>
              </tr>
            </thead>
            <tbody>
              {questions.map((q) => {
                const checked = selectedQuestions.includes(q._id);
                return (
                  <tr key={q._id} className={`asgn-qp__row ${checked ? 'asgn-qp__row--selected' : ''}`} onClick={() => toggleQuestion(q._id)}>
                    <td className="asgn-qp__td asgn-qp__td--check">
                      <input type="checkbox" className="asgn-qp__checkbox" checked={checked} onChange={() => {}} />
                    </td>
                    <td className="asgn-qp__td asgn-qp__td--title">{q.title}</td>
                    <td className="asgn-qp__td">
                      <AppBadge variant="secondary" color={diffColor[q.difficulty]}>{q.difficulty}</AppBadge>
                    </td>
                    <td className="asgn-qp__td asgn-qp__td--cats">
                      {q.categories?.map((c) => (
                        <AppBadge variant="outline" size="sm" key={c._id || c.id || c}>{c.name || c}</AppBadge>
                      ))}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="asgn-qp__pagination">
          <span className="asgn-qp__page-info">Page {page} of {totalPages}</span>
          <div className="asgn-qp__page-btns">
            <button type="button" className="asgn-qp__page-btn" onClick={() => handlePageChange(1)} disabled={page === 1}><ChevronsLeft size={16} /></button>
            <button type="button" className="asgn-qp__page-btn" onClick={() => handlePageChange(page - 1)} disabled={page === 1}><ChevronLeft size={16} /></button>
            <button type="button" className="asgn-qp__page-btn" onClick={() => handlePageChange(page + 1)} disabled={page === totalPages}><ChevronRight size={16} /></button>
            <button type="button" className="asgn-qp__page-btn" onClick={() => handlePageChange(totalPages)} disabled={page === totalPages}><ChevronsRight size={16} /></button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssignmentQuestionPicker;